import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ShoppingBag, Tag } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/apiBase';
import { resolveImageUrl } from '../utils/imageUrl';

const API = API_BASE;

const Cart = () => {
  const { cart, updateCartItem, removeFromCart } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const navigate = useNavigate();

  const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryCharge = 100;
  const discount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const total = subtotal + deliveryCharge - discount;

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity >= 1) {
      updateCartItem(productId, newQuantity);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupon/validate`, {
        code: couponCode,
        subtotal
      });
      setAppliedCoupon(response.data);
      toast.success(`Coupon applied! You saved ₹${response.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.success('Coupon removed');
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout', { state: { couponCode: appliedCoupon?.code } });
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-cream py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <ShoppingBag size={80} className="mx-auto text-golden mb-6" />
            <h1 className="text-4xl font-bold text-dark-text mb-4" data-testid="empty-cart-heading">
              Your Cart is Empty
            </h1>
            <p className="text-gray-700 mb-8">
              Add some products to your cart to see them here.
            </p>
            <Link 
              to="/products" 
              className="btn-primary px-8 py-4 rounded-lg font-semibold text-lg inline-block"
              data-testid="continue-shopping-button"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-dark-text mb-8" data-testid="cart-heading">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-dark-text mb-6">
                Cart Items ({cart.total_items})
              </h2>
              
              <div className="space-y-4">
                {cart.items.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex gap-4 pb-4 border-b last:border-b-0"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Product Image */}
                    <img 
                      src={resolveImageUrl(item.image_url)} 
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    
                    {/* Product Details */}
                    <div className="flex-grow">
                      <h3 className="font-bold text-dark-text mb-1">{item.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {item.category === 'hair' ? 'Hair Care' : 'Skin Care'}
                      </p>
                      <p className="text-lg font-bold text-golden">₹{item.price}</p>
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="flex flex-col items-end gap-4">
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 size={20} />
                      </button>
                      
                      <div className="flex items-center gap-2 border rounded-lg">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="px-3 py-1 hover:bg-gray-100"
                          data-testid={`decrease-qty-${item.id}`}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="px-3 font-semibold" data-testid={`quantity-${item.id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="px-3 py-1 hover:bg-gray-100"
                          data-testid={`increase-qty-${item.id}`}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-dark-text mb-6">Order Summary</h2>
              
              {/* Coupon Code */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-dark-text mb-2">
                  <Tag size={16} className="inline mr-1" />
                  Have a Coupon Code?
                </label>
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                      data-testid="coupon-input"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={validatingCoupon}
                      className="btn-primary px-4 py-2 rounded-lg font-semibold text-sm"
                      data-testid="apply-coupon-button"
                    >
                      {validatingCoupon ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                    <span className="text-green-800 font-semibold">
                      {appliedCoupon.code} Applied!
                    </span>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-red-600 hover:text-red-800"
                      data-testid="remove-coupon-button"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 mb-6 border-t pt-4">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span data-testid="subtotal">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Delivery Charges:</span>
                  <span data-testid="delivery">₹{deliveryCharge.toFixed(2)}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-green-600 font-semibold">
                    <span>Discount ({appliedCoupon.discount_value}%):</span>
                    <span data-testid="discount">- ₹{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold text-dark-text border-t pt-3">
                  <span>Total:</span>
                  <span data-testid="total">₹{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full btn-primary px-6 py-4 rounded-lg font-semibold text-lg"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </button>

              <Link 
                to="/products" 
                className="block text-center text-golden hover:text-dark-text mt-4"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
